// Generated by /app/docs/build_learn_content.py (Spanish: Google Translate, lightly edited). **text** = bold.
export const LEARN_CONTENT = {
  "en": {
    "learn": {
      "eyebrow": "Know",
      "title": "What is radon — and why should I care?",
      "intro": "A practical, five-minute guide for homeowners. Understand the risk, learn how radon gets in, and know what to do about it.",
      "share_title": "What is radon?",
      "share_text": "A five-minute homeowner guide.",
      "faq_link": "Common questions",
      "toc_title": "In this guide",
      "toc": [
        "1. What is radon?",
        "2. How it enters homes",
        "3. Why testing matters"
      ],
      "s1_eyebrow": "1 · What is radon?",
      "s1_title": "A radioactive gas you can't see, smell, or taste.",
      "s1_p1": "Radon is a naturally-occurring radioactive gas that forms when uranium in rock and soil breaks down. It seeps up through the ground and can build up inside homes, schools, and workplaces.",
      "s1_p2": "Because it's **invisible, odorless and tasteless**, the only way to know if radon is in your home is to **test**.",
      "s1_alert": "**Radon is the second leading cause of lung cancer in the United States** after smoking — and the leading cause among non-smokers. The EPA estimates radon causes about **21,000 lung-cancer deaths** in the U.S. every year.",
      "s2_eyebrow": "2 · How it enters homes",
      "s2_title": "Radon rises from the soil into any structure above it.",
      "s2_p1": "Radon gas moves from higher-pressure areas (the soil) to lower-pressure areas (your home). Any opening between your foundation and the ground is a potential path in.",
      "cards": [
        {
          "title": "Cracks in slabs & walls",
          "body": "Hairline cracks in concrete floors and foundation walls are the most common entry points."
        },
        {
          "title": "Construction gaps",
          "body": "Gaps around pipes, wire penetrations, expansion joints, sumps, drains and crawl-space openings."
        },
        {
          "title": "Well water",
          "body": "In some homes, radon dissolves in groundwater and escapes into the air when water is used."
        },
        {
          "title": "Building materials",
          "body": "A small share of homes have radon coming from stone or concrete building materials themselves."
        }
      ],
      "s2_p2": "Radon typically concentrates in the **lowest lived-in level** of a home — basements and first floors are usually higher than upper floors, but every home is different.",
      "s3_eyebrow": "3 · Why testing matters",
      "s3_title": "Radon levels vary enormously — even between neighbors.",
      "s3_p1": "Two identical houses next door to each other can have **very different** radon levels. Soil conditions, foundation age, HVAC setup, and even weather change how much radon accumulates.",
      "s3_p2": "The EPA measures indoor radon in **picocuries per liter (pCi/L)**. Their guidance:",
      "table_head": [
        "pCi/L",
        "Action level",
        "What the EPA recommends"
      ],
      "table_rows": [
        [
          "< 2.0",
          "Below action level",
          "Retest every ~2 years."
        ],
        [
          "2.0 – 3.9",
          "Consider mitigation",
          "Consider fixing; retest periodically."
        ],
        [
          "4.0 – 7.9",
          "Above action level",
          "Fix within a reasonable time frame."
        ],
        [
          "8.0 – 19.9",
          "Well above",
          "Fix promptly."
        ],
        [
          "20+",
          "Very high",
          "Fix immediately."
        ]
      ],
      "s3_p3": "There is **no known completely safe level of radon exposure**. The lower the level in your home, the lower your risk.",
      "state_eyebrow": "For your state",
      "state_fallback": "State radon program",
      "cta_eyebrow": "Ready to know your home's level?",
      "cta_title": "Start your Radon Record in about a minute.",
      "cta_button": "Get started",
      "log_reading": "Log my reading",
      "state_note": "",
      "share_label": "Share this page",
      "copied": "Copied!"
    },
    "faq": {
      "eyebrow": "Learn · FAQ",
      "title": "Common questions about radon.",
      "intro": "Twelve of the questions homeowners ask us most. If you don't see yours,",
      "intro_link": "start with the guide",
      "share_title": "Radon FAQ",
      "share_text": "12 common questions about radon — and answers.",
      "back": "Back to guide",
      "still_eyebrow": "Still not sure?",
      "still_title": "Test your home to know your level.",
      "log_reading": "Log my reading",
      "items": [
        [
          "What does pCi/L mean?",
          "Picocuries per liter — the standard U.S. measurement for radon concentration in air. The EPA action level is 4.0 pCi/L. No level is considered completely safe."
        ],
        [
          "How often should I test my home for radon?",
          "The EPA recommends re-testing every two years, and after any major renovation, HVAC change, or if you move to a new home. Radon levels can shift with the seasons."
        ],
        [
          "What's the difference between short-term and long-term tests?",
          "Short-term tests take 2–7 days and give a quick snapshot. Long-term tests (90+ days) give a more accurate year-round average. If a short-term test is high, confirm with either a follow-up short-term test or a long-term test before mitigating."
        ],
        [
          "How much does radon mitigation cost?",
          "Most residential mitigation systems cost between $800 and $2,500, with a national average around $1,200. Costs vary by home size, foundation type and geography. Systems typically reduce radon by 50–99%."
        ],
        [
          "Is radon actually dangerous?",
          "Yes. Long-term exposure is the second leading cause of lung cancer in the United States and the leading cause among non-smokers. The EPA estimates radon causes about 21,000 lung-cancer deaths every year in the U.S."
        ],
        [
          "Can I test my house myself?",
          "Absolutely. Certified do-it-yourself test kits are available at most hardware stores and online for $15–$40. Follow the placement instructions exactly (lowest lived-in level, closed-house conditions, 20+ inches off the floor). You can also hire a certified radon-measurement professional."
        ],
        [
          "What happens if my radon level is high?",
          "Confirm the result if needed, then hire a certified radon-mitigation professional to install a system (usually sub-slab depressurization). Retest after mitigation to verify it's working. Log every step in your Radon Record so you have proof for future buyers, contractors and grant programs."
        ],
        [
          "Does radon mitigation actually work?",
          "Yes — properly installed systems typically reduce radon by 50–99% and often bring homes well below the 4.0 pCi/L action level. A post-mitigation test confirms the reduction."
        ],
        [
          "Should schools and workplaces test for radon?",
          "Yes. The EPA recommends radon testing in all schools and encourages workplaces to test — especially those with occupied basement or ground-level spaces. Many states have specific requirements."
        ],
        [
          "Does my state have radon requirements?",
          "It depends. Many states have real-estate disclosure laws, some require testing in schools, and some offer free or low-cost test kits. Check your state radon program in the 'For your state' block on our Learn page."
        ],
        [
          "What programs does this site support?",
          "Programs focused on protecting families from radon through education, low-cost test-kit programs (Library Lending), and financial assistance for households who can't afford mitigation."
        ],
        [
          "What is the Radon Community Mitigation Installation Project?",
          "A program that helps qualifying households cover the cost of radon mitigation. Homeowners with elevated levels who meet income and other criteria may qualify for grants or the Sponsor-a-Home waiting list. You can start the screening in your dashboard."
        ]
      ],
      "share_label": "Share this page",
      "copied": "Copied!"
    }
  },
  "es": {
    "learn": {
      "eyebrow": "Conozca",
      "title": "¿Qué es el radón y por qué debería importarme?",
      "intro": "Una guía práctica de cinco minutos para propietarios de viviendas. Comprenda el riesgo, aprenda cómo entra el radón y sepa qué hacer al respecto.",
      "share_title": "¿Qué es el radón?",
      "share_text": "Una guía para propietarios de viviendas de cinco minutos.",
      "faq_link": "Preguntas comunes",
      "toc_title": "En esta guía",
      "toc": [
        "1. ¿Qué es el radón?",
        "2. Cómo entra en los hogares",
        "3. Por qué son importantes las pruebas"
      ],
      "s1_eyebrow": "1 · ¿Qué es el radón?",
      "s1_title": "Un gas radiactivo que no se puede ver, oler ni saborear.",
      "s1_p1": "El radón es un gas radiactivo natural que se forma cuando el uranio en las rocas y el suelo se descompone. Se filtra a través del suelo y puede acumularse dentro de hogares, escuelas y lugares de trabajo.",
      "s1_p2": "Debido a que es **invisible, inodoro e insípido**, la única forma de saber si hay radón en su casa es **hacer la prueba**.",
      "s1_alert": "**El radón es la segunda causa principal de cáncer de pulmón en los Estados Unidos** después del tabaquismo, y la principal causa entre los no fumadores. La EPA estima que el radón causa alrededor de **21,000 muertes por cáncer de pulmón** en los EE. UU. cada año.",
      "s2_eyebrow": "2 · Cómo entra en los hogares",
      "s2_title": "El radón sube desde el suelo a cualquier estructura situada encima de él.",
      "s2_p1": "El gas radón se mueve desde áreas de mayor presión (el suelo) a áreas de menor presión (su hogar). Cualquier abertura entre los cimientos y el suelo es un camino potencial hacia adentro.",
      "cards": [
        {
          "title": "Grietas en losas y paredes",
          "body": "Las grietas finas en pisos de concreto y paredes de cimientos son los puntos de entrada más comunes."
        },
        {
          "title": "Huecos de construcción",
          "body": "Espacios alrededor de tuberías, penetraciones de cables, juntas de expansión, sumideros, desagües y aberturas de espacios de acceso."
        },
        {
          "title": "Agua de pozo",
          "body": "En algunos hogares, el radón se disuelve en el agua subterránea y se escapa al aire cuando se utiliza agua."
        },
        {
          "title": "Materiales de construcción",
          "body": "Una pequeña proporción de hogares tiene radón proveniente de los propios materiales de construcción de piedra u hormigón."
        }
      ],
      "s2_p2": "El radón normalmente se concentra en el **nivel más bajo habitado** de una casa; los sótanos y los primeros pisos suelen ser más altos que los pisos superiores, pero cada casa es diferente.",
      "s3_eyebrow": "3 · Por qué son importantes las pruebas",
      "s3_title": "Los niveles de radón varían enormemente, incluso entre vecinos.",
      "s3_p1": "Dos casas idénticas, una al lado de la otra, pueden tener niveles de radón **muy diferentes**. Las condiciones del suelo, la edad de los cimientos, la configuración de HVAC e incluso el clima cambian la cantidad de radón que se acumula.",
      "s3_p2": "La EPA mide el radón en interiores en **picocurios por litro (pCi/L)**. Sus recomendaciones:",
      "table_head": [
        "pCi/L",
        "Nivel de acción",
        "Lo que recomienda la EPA"
      ],
      "table_rows": [
        [
          "< 2.0",
          "Por debajo del nivel de acción",
          "Vuelva a realizar la prueba cada ~2 años."
        ],
        [
          "2.0 – 3.9",
          "Considere la mitigación",
          "Considere repararlo; vuelva a hacer la prueba periódicamente."
        ],
        [
          "4.0 – 7.9",
          "Por encima del nivel de acción",
          "Reparar en un plazo razonable."
        ],
        [
          "8.0 – 19.9",
          "Muy por encima",
          "Arreglar con prontitud."
        ],
        [
          "20+",
          "Muy alto",
          "Arreglar inmediatamente."
        ]
      ],
      "s3_p3": "**No se conoce ningún nivel completamente seguro de exposición al radón**. Cuanto más bajo sea el nivel en su casa, menor será su riesgo.",
      "state_eyebrow": "Para su estado",
      "state_fallback": "Programa estatal de radón",
      "cta_eyebrow": "¿Listo para conocer el nivel de su casa?",
      "cta_title": "Comience su Registro de radón en aproximadamente un minuto.",
      "cta_button": "Comenzar",
      "log_reading": "Registrar mi lectura",
      "state_note": "La información del programa estatal solo está disponible en inglés.",
      "share_label": "Compartir esta página",
      "copied": "¡Copiado!"
    },
    "faq": {
      "eyebrow": "Aprender · Preguntas frecuentes",
      "title": "Preguntas comunes sobre el radón.",
      "intro": "Doce de las preguntas que más nos hacen los propietarios de viviendas. Si no ve la suya,",
      "intro_link": "comience con la guía",
      "share_title": "Preguntas frecuentes sobre el radón",
      "share_text": "12 preguntas comunes sobre el radón y sus respuestas.",
      "back": "Volver a la guía",
      "still_eyebrow": "¿Aún no está seguro?",
      "still_title": "Haga la prueba en su casa para conocer su nivel.",
      "log_reading": "Registrar mi lectura",
      "items": [
        [
          "¿Qué significa pCi/L?",
          "Picocurios por litro: la medida estándar estadounidense para la concentración de radón en el aire. El nivel de acción de la EPA es 4,0 pCi/L. Ningún nivel se considera completamente seguro."
        ],
        [
          "¿Con qué frecuencia debo realizar pruebas de radón en mi casa?",
          "La EPA recomienda volver a realizar la prueba cada dos años y después de cualquier renovación importante, cambio de HVAC o si se muda a una nueva casa. Los niveles de radón pueden cambiar con las estaciones."
        ],
        [
          "¿Cuál es la diferencia entre pruebas a corto y largo plazo?",
          "Las pruebas a corto plazo duran de 2 a 7 días y brindan una instantánea rápida. Las pruebas a largo plazo (más de 90 días) brindan un promedio anual más preciso. Si una prueba a corto plazo es alta, confirme con una prueba de seguimiento a corto plazo o una prueba a largo plazo antes de mitigar."
        ],
        [
          "¿Cuánto cuesta la mitigación del radón?",
          "La mayoría de los sistemas de mitigación residencial cuestan entre $800 y $2500, con un promedio nacional de alrededor de $1200. Los costos varían según el tamaño de la casa, el tipo de cimientos y la geografía. Los sistemas suelen reducir el radón entre un 50% y un 99%."
        ],
        [
          "¿Es realmente peligroso el radón?",
          "Sí. La exposición prolongada es la segunda causa principal de cáncer de pulmón en los Estados Unidos y la principal causa entre los no fumadores. La EPA estima que el radón causa alrededor de 21.000 muertes por cáncer de pulmón cada año en los EE. UU."
        ],
        [
          "¿Puedo probar mi casa yo mismo?",
          "Absolutamente. Los kits de prueba certificados para que los haga usted mismo están disponibles en la mayoría de las ferreterías y en línea por entre $15 y $40. Siga exactamente las instrucciones de ubicación (nivel más bajo donde se vive, condiciones de casa cerrada, más de 20 pulgadas del piso). También puede contratar a un profesional certificado en medición de radón."
        ],
        [
          "¿Qué pasa si mi nivel de radón es alto?",
          "Confirme el resultado si es necesario, luego contrate a un profesional certificado en mitigación de radón para instalar un sistema (generalmente despresurización debajo de la losa). Vuelva a realizar la prueba después de la mitigación para verificar que esté funcionando. Registre cada paso en su Registro de radón para tener pruebas para futuros compradores, contratistas y programas de subvenciones."
        ],
        [
          "¿Funciona realmente la mitigación del radón?",
          "Sí, los sistemas correctamente instalados suelen reducir el radón entre un 50% y un 99% y, a menudo, dejan los hogares muy por debajo del nivel de acción de 4,0 pCi/L. Una prueba posterior a la mitigación confirma la reducción."
        ],
        [
          "¿Deberían las escuelas y los lugares de trabajo realizar pruebas de detección de radón?",
          "Sí. La EPA recomienda pruebas de radón en todas las escuelas y alienta a los lugares de trabajo a realizar pruebas, especialmente aquellos con sótanos ocupados o espacios a nivel del suelo. Muchos estados tienen requisitos específicos."
        ],
        [
          "¿Mi estado tiene requisitos de radón?",
          "Eso depende. Muchos estados tienen leyes de divulgación de bienes raíces, algunos exigen pruebas en las escuelas y otros ofrecen kits de prueba gratuitos o de bajo costo. Consulte el programa de radón de su estado en el bloque \"Para su estado\" en nuestra guía."
        ],
        [
          "¿Qué programas admite este sitio?",
          "Programas centrados en proteger a las familias del radón a través de la educación, programas de kits de prueba de bajo costo (préstamo de bibliotecas) y asistencia financiera para hogares que no pueden permitirse la mitigación."
        ],
        [
          "¿Qué es el Proyecto de instalación de mitigación comunitaria de radón?",
          "Un programa que ayuda a los hogares que califican a cubrir el costo de la mitigación del radón. Los propietarios de viviendas con niveles elevados que cumplan con los ingresos y otros criterios pueden calificar para subvenciones o para la lista de espera de Patrocine una casa. Puede iniciar la evaluación en su panel de control."
        ]
      ],
      "share_label": "Compartir esta página",
      "copied": "¡Copiado!"
    }
  }
};
